"use client";
import { trackGizmo } from "./analytics";
import { defaultKibbePreferences, KibbePreferences, KibbePreview, kibbePreviewSchema, kibbeName, kibbeSlug } from "./kibbe";
import { KibbeCheckCategory, kibbeHttpCategory, kibbePhotoResponseSchema, kibbeSuitabilitySchema } from "./kibbePhotoCheck";

export const KIBBE_FLOW = "kibbe-v2";
export class KibbeFlowError extends Error { constructor(public code: "storage" | "rateLimit" | "photo" | "preview" | "checkout", public userMessage?: string, public category?: KibbeCheckCategory) { super(userMessage || code); } }
export function kibbeErrorText(error: unknown, translate: (key: string) => string, fallback = "preview") { return error instanceof KibbeFlowError ? error.userMessage || translate(error.code) : translate(fallback); }
export type KibbeDraft = { image: string; portrait: string; preferences: KibbePreferences; includeColor: boolean };
export const emptyKibbeDraft: KibbeDraft = { image: "", portrait: "", preferences: defaultKibbePreferences, includeColor: false };
export function readKibbeDraft(): KibbeDraft {
  try {
    const saved = JSON.parse(sessionStorage.getItem("kibbeDraftV2") || "null");
    return { ...emptyKibbeDraft, ...saved, image: typeof saved?.image === "string" ? saved.image : sessionStorage.getItem("kibbeAnalysisImage") || "", preferences: { ...defaultKibbePreferences, ...saved?.preferences } };
  } catch { return { ...emptyKibbeDraft, preferences: { ...defaultKibbePreferences } }; }
}
export function saveKibbeDraft(draft: KibbeDraft) {
  try { sessionStorage.setItem("kibbeDraftV2", JSON.stringify(draft)); } catch { throw new KibbeFlowError("storage"); }
}
// Payment/fulfillment still use KIBBE_FLOW v2. Analytics describes the v3 experience.
const SOURCES = ["type_guide", "comparison_guide", "outfit_guide", "menswear_guide", "style_quiz_result", "visual_campaign", "color_result"];
const PLACEMENTS = ["hero", "inline", "footer", "result", "free_result", "type_choice", "comparison", "outfit", "menswear", "campaign"];
export function kibbeEntryContext(query: URLSearchParams): Record<string, string> {
  const context: Record<string, string> = {};
  if (SOURCES.includes(query.get("source") || "")) context.source = query.get("source")!;
  if (PLACEMENTS.includes(query.get("placement") || "")) context.placement = query.get("placement")!;
  const content = query.get("content"), contentType = kibbeName(content);
  if (contentType) context.content = kibbeSlug(contentType);
  else if (content && ["menswear", "style-personality", "kibbe", "color-analysis"].includes(content)) context.content = content;
  for (const key of ["focus", "knownType", "typeA", "typeB"]) { const type = kibbeName(query.get(key)); if (type) context[key] = type; }
  return context;
}
export function kibbeAnalyticsProps(props: Record<string, string | number | boolean>) {
  const safe: Record<string, string | number | boolean> = {};
  for (const key of ["known_type", "color_addon", "cache_hit", "portrait"]) if (typeof props[key] === "boolean") safe[key] = props[key];
  if (typeof props.duration_ms === "number" && Number.isFinite(props.duration_ms)) safe.duration_ms = Math.min(600000, Math.max(0, Math.round(props.duration_ms)));
  const values: Record<string, readonly string[]> = {
    clothing: ["womenswear", "menswear", "mixed"],
    category: ["height", "photo", "preferences", "storage", "format", "size", "decode", "invalid_image", "invalid_input", "rate_limit", "unsuitable", "timeout", "network", "http", "response", "unknown", "preview", "checkout", "origin", "provider", "schema", "refusal", "incomplete"],
    stage: ["preview", "suitability"], placement: PLACEMENTS, source: SOURCES, action: ["analysis", "styling", "sample"],
  };
  for (const [key, allowed] of Object.entries(values)) if (typeof props[key] === "string" && allowed.includes(props[key] as string)) safe[key] = props[key];
  for (const key of ["type", "type_b"]) if (typeof props[key] === "string") { const type = kibbeName(props[key] as string); if (type) safe[key] = type; }
  if (typeof props.content === "string") {
    const type = kibbeName(props.content);
    if (type) safe.content = kibbeSlug(type);
    else if (["menswear", "style-personality", "kibbe", "color-analysis"].includes(props.content)) safe.content = props.content;
  }
  return safe;
}
export function kibbeTrackingContext(): Record<string, string> {
  let context: Record<string, string> = {};
  if (typeof window !== "undefined") {
    try {
      const incoming = kibbeEntryContext(new URLSearchParams(window.location.search));
      const saved = kibbeEntryContext(new URLSearchParams(sessionStorage.getItem("kibbeEntryV3") || ""));
      context = incoming.source ? incoming : saved;
      if (incoming.source) sessionStorage.setItem("kibbeEntryV3", new URLSearchParams(incoming).toString());
    } catch { /* Attribution never blocks the flow. */ }
  }
  return { ...context, ...(context.source ? { origin_source: context.source } : {}), flow_version: "kibbe-v3" };
}
export const trackKibbe = (step: string, props: Record<string, string | number | boolean> = {}) => {
  trackGizmo(`kibbe_${step}`, { ...kibbeTrackingContext(), ...kibbeAnalyticsProps(props) });
};
export const kibbeErrorCategory = (error: unknown) => error instanceof KibbeFlowError ? error.category || error.code : "unknown";

export class KibbePhotoError extends Error { constructor(public code: "format" | "size" | "decode") { super(code); } }
export async function prepareKibbePhoto(file: File): Promise<string> {
  const heic = /image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
  if (!heic && !["image/jpeg", "image/png", "image/webp"].includes(file.type)) throw new KibbePhotoError("format");
  if (file.size > 10 * 1024 * 1024) throw new KibbePhotoError("size");
  // Safari can decode HEIC natively. Load the decoder only when needed.
  try { return await resizeKibbePhoto(file); } catch { if (!heic) throw new KibbePhotoError("decode"); }
  try {
    const { heicTo } = await import("heic-to/csp");
    const jpeg = await heicTo({ blob: file, type: "image/jpeg", quality: 0.9 });
    return await resizeKibbePhoto(jpeg);
  } catch { throw new KibbePhotoError("decode"); }
}
function resizeKibbePhoto(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file), image = new Image();
    image.onerror = () => { URL.revokeObjectURL(url); reject(new Error("This photo could not be opened. Try another JPG or PNG.")); };
    image.onload = () => {
      try {
        const scale = Math.min(1400 / Math.max(image.width, image.height), 1);
        const canvas = document.createElement("canvas"); canvas.width = Math.round(image.width * scale); canvas.height = Math.round(image.height * scale);
        const context = canvas.getContext("2d"); if (!context) throw new Error("Photo processing unavailable.");
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      } catch (error) { reject(error); } finally { URL.revokeObjectURL(url); }
    };
    image.src = url;
  });
}

const checkRequests = new Map<string, Promise<unknown>>();
async function photoKey(image: string) {
  const bytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(image));
  return Array.from(new Uint8Array(bytes), byte => byte.toString(16).padStart(2, "0")).join("");
}
function cacheRead(key: string) { try { return JSON.parse(sessionStorage.getItem(key) || "null"); } catch { return null; } }
function cacheWrite(key: string, value: unknown) { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* Checks work without storage. */ } }
function cacheSuitability(key: string) { cacheWrite("kibbeSuitabilityV3", { key, suitable: true }); }
function migrateLegacyPreview(image: string, key: string) {
  const legacy = cacheRead("kibbePreviewV2");
  if (legacy?.image !== image) return;
  const parsed = kibbePreviewSchema.safeParse(legacy.result);
  if (!parsed.success || parsed.data.primaryType === parsed.data.secondaryType) return;
  cacheWrite("kibbePreviewV3", { key, heightCm: legacy.heightCm || null, locale: legacy.locale, result: parsed.data });
  cacheSuitability(key);
  try { sessionStorage.removeItem("kibbePreviewV2"); } catch { /* Existing legacy storage can expire normally. */ }
}
async function requestCheck(image: string, mode: "preview" | "suitability", heightCm?: number, locale = "en") {
  const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 40000);
  try {
    const response = await fetch("/api/preliminary-kibbe-analysis", { method: "POST", signal: controller.signal, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ imageData: image, heightCm, locale, mode }) });
    let data;
    try { data = await response.json(); } catch {
      if (controller.signal.aborted) throw new KibbeFlowError("preview", undefined, "timeout");
      if (response.ok) throw new KibbeFlowError("preview", undefined, "response");
    }
    if (!response.ok) { const category = kibbeHttpCategory(response.status, data?.code); throw new KibbeFlowError(category === "rate_limit" ? "rateLimit" : "preview", undefined, category); }
    if (data?.success !== true) throw new KibbeFlowError("preview", undefined, "response");
    const parsed = (mode === "suitability" ? kibbeSuitabilitySchema : kibbePhotoResponseSchema).safeParse(data.result);
    if (!parsed.success) throw new KibbeFlowError("preview", undefined, "response");
    if (!parsed.data.suitable) throw new KibbeFlowError("photo", parsed.data.reason, "unsuitable");
    return parsed.data;
  } catch (error) {
    if (error instanceof KibbeFlowError) throw error;
    throw new KibbeFlowError("preview", undefined, controller.signal.aborted ? "timeout" : "network");
  } finally { clearTimeout(timer); }
}
export async function checkKibbePhoto(image: string, locale = "en"): Promise<void> {
  const key = await photoKey(image), started = Date.now();
  migrateLegacyPreview(image, key);
  if (cacheRead("kibbeSuitabilityV3")?.key === key && cacheRead("kibbeSuitabilityV3")?.suitable === true) { trackKibbe("photo_check_complete", { stage: "suitability", cache_hit: true, duration_ms: Date.now() - started }); return; }
  const requestKey = `suitability:${key}`;
  let pending = checkRequests.get(requestKey);
  if (!pending) { pending = requestCheck(image, "suitability", undefined, locale).then(() => cacheSuitability(key)).finally(() => checkRequests.delete(requestKey)); checkRequests.set(requestKey, pending); }
  await pending;
  trackKibbe("photo_check_complete", { stage: "suitability", cache_hit: false, duration_ms: Date.now() - started });
}
export async function getKibbePreview(image: string, heightCm?: number, locale = "en"): Promise<KibbePreview> {
  const key = await photoKey(image), height = heightCm || null;
  migrateLegacyPreview(image, key);
  const cached = cacheRead("kibbePreviewV3");
  if (cached?.key === key && cached?.heightCm === height && cached?.locale === locale) {
    const parsed = kibbePreviewSchema.safeParse(cached.result);
    if (parsed.success && parsed.data.primaryType !== parsed.data.secondaryType) { cacheSuitability(key); return parsed.data; }
  }
  const requestKey = `preview:${key}:${height}:${locale}`;
  let pending = checkRequests.get(requestKey);
  if (!pending) {
    pending = requestCheck(image, "preview", heightCm, locale).then(result => {
      const parsed = kibbePreviewSchema.parse(result);
      cacheWrite("kibbePreviewV3", { key, heightCm: height, locale, result: parsed }); cacheSuitability(key); return parsed;
    }).finally(() => checkRequests.delete(requestKey));
    checkRequests.set(requestKey, pending);
  }
  return await pending as KibbePreview;
}
